import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { Offcanvas, Button } from "react-bootstrap";
import { useApp } from "../../contexts/AppContext";
import {
  fetchStructuredReport,
  fetchAllSections,
  createItem,
  deleteItem,
  aiGenerateItem,
  closeNewItemModal,
  addAIProcessingItem,
  pollTaskStatus,
  compileReport,
  uploadCoverPhoto,
  exitEdit,
  clearFailedAIProcessing,
} from "./slice";
import { registerRetryPayload } from "./retryRegistry";
import EditDrawer from "./editDrawer";
import EditContent from "./editContent";
import EditHeader from "./editHeader";
import EditConfirmModal from "./editConfirmModal";
import DiscardChangesModal from "app/components/common/DiscardChangesModal";
import AddNewItemModal from "./addNewItemModal";
import LoadingSpinner from "../../components/LoadingSpinner";
import CustomToast from "../../components/common/CustomToast";
import burgerMenu from "../../images/burger-menu.svg";
import { FREE_TIER_REPORTS_QUOTA } from "app/constants/global-constants";
import "./webReportEdit.css";

const WebReportEdit = () => {
  const [showDrawer, setShowDrawer] = useState(false);
  const [scrolledPastCover, setScrolledPastCover] = useState(false);
  const coverPhotoRef = useRef(null);
  const activeSection = null;

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { reportId } = useParams();
  const {
    sidebar: { setForceOverlay },
    device,
  } = useApp();

  const [showDiscardModal, setShowDiscardModal] = useState(false);
  // Holds the deferred destination while the discard prompt is open: an href
  // string for an in-app link, or "back" for an intercepted browser Back.
  const [pendingNavigation, setPendingNavigation] = useState(null);

  const [toast, setToast] = useState({
    show: false,
    message: "",
    variant: "success",
  });

  const { reportContent, requestStatus, newItemModal, loading } = useSelector(
    (state) => state.webReportEdit
  );
  const editingTypeById = useSelector(
    (state) => state.webReportEdit.edit.editingTypeById
  );
  const dirtyById = useSelector((state) => state.webReportEdit.edit.dirtyById);
  // Ids of every item currently open in edit mode (multiple allowed).
  const editingItemIds = Object.keys(editingTypeById);
  // Dirty if ANY open item has unsaved changes — drives the navigation guard.
  const isDirty = Object.values(dirtyById).some(Boolean);
  // Unresolved failed items also block a clean leave (AS-902 D8): they're
  // session-scoped, so warn before the inspector abandons them.
  const aiProcessingItems = useSelector(
    (state) => state.webReportEdit.aiProcessing.items
  );
  const hasFailedItems = aiProcessingItems.some(
    (i) => i.taskState === "FAILURE"
  );
  const shouldGuardLeave = isDirty || hasFailedItems;
  // Mirror the guard flag into a ref so the imperative navigation listeners
  // (beforeunload, capture-phase click, popstate) read the latest value
  // without re-subscribing on every change.
  const isDirtyRef = useRef(false);
  useEffect(() => {
    isDirtyRef.current = shouldGuardLeave;
  }, [shouldGuardLeave]);
  // Set just before we intentionally call history.go() on a confirmed Back,
  // so the popstate guard ignores that programmatic navigation.
  const isLeavingRef = useRef(false);
  // Free-tier users see the "1 of N remaining credit" line on the
  // Compile Report confirm step. Paid plans get unlimited generations.
  const userProfile = useSelector((state) => state.userProfile?.profile);
  const isFreeTier = userProfile?.subscription?.tier === "Free";
  const remainingCredits = Math.max(
    FREE_TIER_REPORTS_QUOTA - (userProfile?.monthly_compiled_reports || 0),
    0
  );

  const [compileModalStep, setCompileModalStep] = useState(null);

  useEffect(() => {
    setForceOverlay(true);
    return () => setForceOverlay(false);
  }, [setForceOverlay]);

  useEffect(() => {
    if (reportId) {
      dispatch(fetchStructuredReport(reportId));
      dispatch(fetchAllSections(reportId));
    }
  }, [dispatch, reportId]);

  useEffect(() => {
    const address = reportContent?.address;
    const displayTitle = address?.formatted_address
      ? `Edit - ${address.formatted_address}`
      : "Edit Report";
    document.title = displayTitle;
  }, [reportContent]);

  // Toggle header into its "scrolled" state once the cover photo leaves the
  // viewport. rootMargin offsets by the fixed header's ~100px height.
  useEffect(() => {
    const el = coverPhotoRef.current;
    if (!el) return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => setScrolledPastCover(!entry.isIntersecting),
      { rootMargin: "-100px 0px 0px 0px", threshold: 0 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [reportContent]);

  const handleShowDrawer = () => setShowDrawer(true);
  const handleCloseDrawer = () => setShowDrawer(false);

  // Nearest actually-scrollable ancestor (depends on content height — could be
  // `.app-main-content` or the window), so we don't hardcode the wrong one.
  const getScrollParent = (node) => {
    let cur = node?.parentElement;
    while (cur) {
      const { overflowY } = window.getComputedStyle(cur);
      if (
        (overflowY === "auto" || overflowY === "scroll") &&
        cur.scrollHeight > cur.clientHeight
      ) {
        return cur;
      }
      cur = cur.parentElement;
    }
    return null;
  };

  // Scroll an item below the fixed sticky edit-header. Working in viewport
  // coordinates (item rect.top vs the header's rect.bottom) cancels the
  // scroller's own top offset and reads the header's real height, so it stays
  // correct on small screens where the header reflows taller.
  const pendingScrollIdRef = useRef(null);
  const scrollToEditItem = (itemId) => {
    const el = document.getElementById(itemId);
    if (!el) return;
    const header = document.querySelector(".edit-header");
    const headerBottom = header ? header.getBoundingClientRect().bottom : 156;
    const GAP = 16;
    const delta = el.getBoundingClientRect().top - headerBottom - GAP;
    const scroller = getScrollParent(el);
    if (scroller) {
      scroller.scrollBy({ top: delta, behavior: "smooth" });
    } else {
      window.scrollBy({ top: delta, behavior: "smooth" });
    }
  };

  // Shared by the drawer's section/subsection nav and the AI-processing panel.
  // On mobile the drawer is a scroll-locked overlay covering the content, so
  // close it first and defer the scroll to the Offcanvas `onExited`. On desktop
  // the drawer is static — scroll now.
  const handleDrawerNavigate = (targetId) => {
    if (!targetId) return;
    const isOverlay = window.matchMedia("(max-width: 991px)").matches;
    if (isOverlay && showDrawer) {
      pendingScrollIdRef.current = targetId;
      setShowDrawer(false);
    } else {
      scrollToEditItem(targetId);
    }
  };

  const handleCancelDiscard = () => {
    setShowDiscardModal(false);
    setPendingNavigation(null);
  };

  // Confirmed "Leave": clear the dirty state, then perform the deferred
  // navigation. For an intercepted Back we go past both the sentinel entry
  // (pushed by the popstate guard) and the editor entry.
  const handleConfirmDiscard = () => {
    const target = pendingNavigation;
    setShowDiscardModal(false);
    setPendingNavigation(null);
    dispatch(exitEdit());
    // Session-scoped failed cards are dropped on leave so they don't re-render
    // if the inspector navigates back into the editor (AS-902 D8).
    dispatch(clearFailedAIProcessing());
    if (target === "back") {
      isLeavingRef.current = true;
      window.history.go(-2);
    } else if (target) {
      navigate(target);
    }
  };

  // Warn on browser refresh / tab close / external navigation.
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (!isDirtyRef.current) return;
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, []);

  // Intercept in-app link clicks (global sidebar, page-level links). Hash
  // links (#section) and external (http) links are left alone.
  useEffect(() => {
    const handleClick = (e) => {
      if (!isDirtyRef.current) return;
      const link = e.target.closest("a[href]");
      if (!link) return;
      const href = link.getAttribute("href");
      if (!href || href.startsWith("#") || href.startsWith("http")) return;
      e.preventDefault();
      e.stopPropagation();
      setPendingNavigation(href);
      setShowDiscardModal(true);
    };
    document.addEventListener("click", handleClick, true);
    return () => document.removeEventListener("click", handleClick, true);
  }, []);

  // Block the SPA Back button. This app uses BrowserRouter (no data router),
  // so useBlocker isn't available — instead keep a sentinel history entry
  // while dirty and re-push it on popstate so Back lands here and prompts.
  useEffect(() => {
    if (!shouldGuardLeave) return undefined;
    window.history.pushState(null, document.title, window.location.href);
    const handlePopState = () => {
      if (isLeavingRef.current) return;
      window.history.pushState(null, document.title, window.location.href);
      setPendingNavigation("back");
      setShowDiscardModal(true);
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [shouldGuardLeave]);

  const handleSelectCoverPhotoFile = async (file) => {
    try {
      await dispatch(uploadCoverPhoto({ reportId, file })).unwrap();
      setToast({
        show: true,
        message: "Cover photo updated.",
        variant: "success",
      });
    } catch (err) {
      setToast({
        show: true,
        message:
          typeof err === "string" ? err : "Failed to upload cover photo.",
        variant: "error",
      });
    }
  };

  // Compile flow gates before POST /compile/:
  //   unsaved edits → missingCover → confirm.
  const coverImageUrl = reportContent?.profile_image?.presigned_url;

  const handleCompile = () => {
    if (editingItemIds.length > 0) {
      setCompileModalStep("unsaved");
      return;
    }
    if (!coverImageUrl) {
      setCompileModalStep("missingCover");
      return;
    }
    setCompileModalStep("confirm");
  };

  const closeCompileModal = () => setCompileModalStep(null);

  const handleReviewEdits = () => {
    closeCompileModal();
    if (editingItemIds.length === 0) return;
    // Scroll to the first open item so the user can review/resolve edits.
    const el = document.getElementById(editingItemIds[0]);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleAddCoverPhoto = () => {
    closeCompileModal();
    const el = document.getElementById("inspection-overview");
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handleSkipCoverPhoto = () => setCompileModalStep("confirm");

  const cancelAction = {
    label: "Cancel",
    variant: "outline-secondary",
    onClick: closeCompileModal,
  };
  const compileModalConfigs = {
    unsaved: {
      title: "Unsaved edits detected",
      body: (
        <p>You have items still under revision. Save changes to compile.</p>
      ),
      actions: [
        cancelAction,
        {
          label: "Review Edits",
          variant: "primary",
          onClick: handleReviewEdits,
        },
      ],
    },
    missingCover: {
      title: "Cover Photo Missing",
      body: (
        <p>
          The inspection results doesn&apos;t include a cover photo. Do you want
          to skip it and proceed with compilation?
        </p>
      ),
      actions: [
        { ...cancelAction, slot: "left" },
        {
          label: "Skip",
          variant: "outline-primary",
          onClick: handleSkipCoverPhoto,
        },
        {
          label: "Add Photo",
          variant: "primary",
          onClick: handleAddCoverPhoto,
        },
      ],
    },
    confirm: {
      title: "Compile Report",
      requireConfirmation:
        "I verify that the content in this report is true and accurate.",
      body: (
        <>
          <p>
            Assemble items into a report for delivery. This action will replace
            any existing report.
          </p>
          {isFreeTier && (
            <p className="edit-compile-credit">
              <strong>
                * 1 of {remainingCredits} remaining{" "}
                {remainingCredits === 1 ? "credit" : "credits"} will be applied
              </strong>
            </p>
          )}
        </>
      ),
      actions: [
        cancelAction,
        {
          label: "OK",
          variant: "primary",
          requiresConfirm: true,
          onClick: () => handleConfirmCompile(),
        },
      ],
    },
  };
  const compileModalProps = compileModalStep
    ? compileModalConfigs[compileModalStep]
    : null;

  const handleConfirmCompile = () => {
    closeCompileModal();
    dispatch(compileReport(reportId))
      .unwrap()
      .then((data) => {
        // compiled-reports/ is a public route; REACT_APP_URL points to the
        // public-facing host (the editor may be hosted elsewhere).
        const baseUrl = process.env.REACT_APP_URL || window.location.origin;
        const compiledUrl = `${baseUrl}/compiled-reports/${data.id}/`;
        setToast({
          show: true,
          message: "Report compiled successfully.",
          variant: "success",
          actionLabel: "View",
          onAction: () => window.open(compiledUrl, "_blank"),
        });
      })
      .catch(() => {
        setToast({
          show: true,
          message: "Could not compile report. Please try again.",
          variant: "error",
        });
      });
  };

  const handleSelectNewItemType = (type) => {
    dispatch(createItem({ type, reportId }))
      .unwrap()
      .catch(() => {
        setToast({
          show: true,
          message: "Could not start new item. Please try again.",
          variant: "error",
        });
      });
  };

  const handleCancelNewItem = () => {
    const { type, itemId } = newItemModal;
    dispatch(closeNewItemModal());
    if (type && itemId) {
      dispatch(deleteItem({ type, itemId }));
    }
  };

  const handleSubmitNewItem = ({
    images,
    annotatedImages,
    annotations,
    descriptionHint,
    concernLevel,
  }) => {
    const { type, itemId } = newItemModal;
    const photoCount = images?.length || 0;
    // Blob URLs preview the user's photos while AI analyzes them — show the
    // annotated version where present. The slice revokes them when the item is
    // removed or transitions to a terminal state.
    const photoUrls = (images || []).map((file, i) =>
      URL.createObjectURL(annotatedImages?.[i] || file)
    );
    dispatch(
      aiGenerateItem({
        type,
        reportId,
        itemId,
        images,
        annotatedImages,
        annotations,
        descriptionHint,
        concernLevel,
      })
    )
      .unwrap()
      .then((data) => {
        dispatch(
          addAIProcessingItem({
            taskId: data.task_id,
            itemId: data.item_id,
            type,
            concernLevel: concernLevel ?? null,
            photoCount,
            photoUrls,
            displayName: descriptionHint || null,
            taskState: "PENDING",
            itemStatus: "empty",
            error: null,
          })
        );
        // Register the submission so a failure can be retried against the same
        // (existing) item without re-uploading. aiGenerateItem already runs in
        // existing-item mode here (itemId is the pre-created item).
        registerRetryPayload(data.task_id, {
          kind: "create",
          reportId,
          args: {
            type,
            reportId,
            itemId: data.item_id || itemId,
            images,
            annotatedImages,
            annotations,
            descriptionHint,
            concernLevel,
          },
        });
        dispatch(pollTaskStatus({ taskId: data.task_id, reportId }));
        dispatch(closeNewItemModal());
        setToast({
          show: true,
          message: "Item created. Keep editing while AI processes.",
          variant: "success",
        });
      })
      .catch(() => {
        photoUrls.forEach((url) => URL.revokeObjectURL(url));
        setToast({
          show: true,
          message: "AI analysis failed to start. Please try again.",
          variant: "error",
        });
      });
  };

  // Block only on the initial fetch. Silent refetches (after an AI task
  // completes) must not flash the page into a loading state.
  const hasInitialContent = !!reportContent?.id;
  if (!hasInitialContent && requestStatus !== "fail") {
    return <LoadingSpinner type="fullPage" />;
  }

  if (!hasInitialContent && requestStatus === "fail") {
    return (
      <div className="edit-error-state">
        <p>Failed to load report. Please try again.</p>
      </div>
    );
  }

  const sections = reportContent?.sections || [];
  const address = reportContent?.address;

  return (
    <div className="web-report-edit-container">
      <div className="edit-burger-container d-lg-none">
        <Button variant="primary" onClick={handleShowDrawer}>
          <div>
            <img src={burgerMenu} alt="menu" />
          </div>
        </Button>
      </div>

      <Offcanvas
        show={showDrawer}
        onHide={handleCloseDrawer}
        responsive="lg"
        backdrop={true}
        scroll={false}
        className="edit-drawer-offcanvas"
        onExited={() => {
          if (pendingScrollIdRef.current) {
            scrollToEditItem(pendingScrollIdRef.current);
            pendingScrollIdRef.current = null;
          }
        }}
      >
        <Offcanvas.Body>
          <EditDrawer
            sections={sections}
            activeSection={activeSection}
            onNavigate={handleDrawerNavigate}
          />
        </Offcanvas.Body>
      </Offcanvas>

      <div className="edit-drawer-spacer d-none d-lg-block" />

      <div className="edit-main-content-wrapper">
        <EditHeader
          address={address}
          coverImageUrl={coverImageUrl}
          scrolledPastCover={scrolledPastCover}
          onSelectNewItemType={handleSelectNewItemType}
          newItemLoading={loading.createItem}
          onCompile={handleCompile}
          compileLoading={loading.compileReport}
        />
        <EditContent
          reportContent={reportContent}
          coverPhotoRef={coverPhotoRef}
          onSelectCoverPhotoFile={handleSelectCoverPhotoFile}
          coverUploadLoading={loading.uploadCoverPhoto}
          onToast={({ message, variant }) =>
            setToast({ show: true, message, variant })
          }
        />
      </div>

      <AddNewItemModal
        show={newItemModal.isOpen}
        type={newItemModal.type}
        onCancel={handleCancelNewItem}
        onSubmit={handleSubmitNewItem}
        isSubmitting={loading.aiGenerate}
      />

      <EditConfirmModal
        show={!!compileModalStep}
        onHide={closeCompileModal}
        title={compileModalProps?.title}
        actions={compileModalProps?.actions}
        requireConfirmation={compileModalProps?.requireConfirmation}
      >
        {compileModalProps?.body}
      </EditConfirmModal>

      <DiscardChangesModal
        show={showDiscardModal}
        onHide={handleCancelDiscard}
        onConfirm={handleConfirmDiscard}
      />

      <CustomToast
        show={toast.show}
        onClose={() => setToast((t) => ({ ...t, show: false }))}
        message={toast.message}
        variant={toast.variant}
        isMobile={device.isMobile}
        actionLabel={toast.actionLabel}
        onAction={toast.onAction}
      />

      {/* Full-page mask blocks click-through while compile is in flight. */}
      {loading.compileReport && <LoadingSpinner type="masked" />}
    </div>
  );
};

export default WebReportEdit;
